import type { AccordionTriggerProps } from "./types";
import { useAccordion, useAccordionItem } from "./AccordionContext";
import { ChevronToggle } from "@/components/ui/buttons/ChevronToggle";
import { cx } from "@/utils/cx";
import styles from "./Accordion.module.css";

/**
 * Accordion.Trigger component
 *
 * The clickable header that toggles the accordion item.
 * Includes a built-in chevron indicator that rotates when open.
 *
 * @example
 * ```tsx
 * <Accordion.Trigger>Click to expand</Accordion.Trigger>
 * <Accordion.Trigger hideChevron>No chevron</Accordion.Trigger>
 * <Accordion.Trigger chevronPosition="start">Start chevron</Accordion.Trigger>
 * <Accordion.Trigger chevronVariant="toggle">Green button style</Accordion.Trigger>
 * ```
 */
export function AccordionTrigger({
  hideChevron = false,
  chevronPosition = "end",
  chevronVariant = "iconOnly",
  className,
  chevronClassName,
  "data-testid": testId,
  children,
}: AccordionTriggerProps) {
  const accordion = useAccordion();
  const item = useAccordionItem();

  const handleClick = () => {
    if (!item.isDisabled) {
      accordion.onToggle(item.value);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (item.isDisabled) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      accordion.onToggle(item.value);
    }
  };

  const chevron = !hideChevron && (
    <ChevronToggle
      isOpen={item.isOpen}
      onClick={() => accordion.onToggle(item.value)}
      iconOnly
      variant={chevronVariant === "iconOnly" ? undefined : chevronVariant}
      className={cx(styles.chevron, chevronClassName)}
    />
  );

  return (
    <button
      type="button"
      id={item.triggerId}
      className={cx(styles.trigger, className)}
      aria-expanded={item.isOpen}
      aria-controls={item.contentId}
      disabled={item.isDisabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-accordion-trigger
      data-state={item.isOpen ? "open" : "closed"}
      data-disabled={item.isDisabled ? "" : undefined}
      data-testid={testId}
    >
      {chevronPosition === "start" && chevron}
      <span className={styles.triggerContent}>{children}</span>
      {chevronPosition === "end" && chevron}
    </button>
  );
}
